// Coordinador del Enlace Geométrico — escucha eventos del server y
// orquesta visuales + WebRTC.
//
// Vive como hook de App (mounted una sola vez). Conecta:
//   - useLinkStore (estado UI)
//   - useNetworkStore (anchor de peers)
//   - usePlayerStore (anchor mío)
//   - lib/webrtc (audio P2P)

import { useEffect } from "react"
import {
    SOCKET_EVENTS,
    type AnchorBroadcast,
    type LinkAbortedBroadcast,
    type LinkIncomingBroadcast,
    type LinkIncomingLostBroadcast,
    type LinkReciprocatedBroadcast,
    type LinkSealedBroadcast,
    type WebrtcAnswerIncoming,
    type WebrtcIceIncoming,
    type WebrtcOfferIncoming,
} from "@domo/shared"
import { getSocket } from "../lib/socket"
import {
    closeVoiceConnection,
    handleIncomingAnswer,
    handleIncomingIce,
    handleIncomingOffer,
    openVoiceConnection,
} from "../lib/webrtc"
import { useLinkStore } from "../stores/useLinkStore"
import { useNetworkStore } from "../stores/useNetworkStore"
import { usePlayerStore } from "../stores/usePlayerStore"

export function useLink(connected: boolean) {
    useEffect(() => {
        if (!connected) return
        const socket = getSocket()
        if (!socket) return

        const link = useLinkStore.getState()
        const net = useNetworkStore.getState()
        const player = usePlayerStore.getState()

        // ── Llave doble ───────────────────────────────────────────────

        const onIncoming = (b: LinkIncomingBroadcast) => {
            link.addIncoming(b.fromId)
        }
        const onIncomingLost = (b: LinkIncomingLostBroadcast) => {
            link.removeIncoming(b.fromId)
        }
        const onReciprocated = (b: LinkReciprocatedBroadcast) => {
            link.startSealing(b.peerId)
        }
        const onSealed = (b: LinkSealedBroadcast) => {
            link.sealCompleted(b.peerId, b.isInitiator)
            // Abrir voz P2P. Iniciador crea oferta; el otro espera.
            openVoiceConnection({
                socket,
                peerId: b.peerId,
                isInitiator: b.isInitiator,
            })
        }
        const onAborted = (b: LinkAbortedBroadcast) => {
            closeVoiceConnection()
            link.abort()
        }

        // ── Anclaje (otros + yo mismo) ────────────────────────────────

        const onAnchor = (b: AnchorBroadcast) => {
            if (b.id === useNetworkStore.getState().selfId) {
                player.setAnchored(b.isAnchored)
            } else {
                net.updatePeerAnchor(b.id, b.isAnchored)
            }
        }

        // ── WebRTC signaling ──────────────────────────────────────────

        const onOffer = (b: WebrtcOfferIncoming) => {
            handleIncomingOffer(socket, b.from, b.sdp)
        }
        const onAnswer = (b: WebrtcAnswerIncoming) => {
            handleIncomingAnswer(b.from, b.sdp)
        }
        const onIce = (b: WebrtcIceIncoming) => {
            handleIncomingIce(b.from, b.candidate)
        }

        socket.on(SOCKET_EVENTS.LINK_INCOMING, onIncoming)
        socket.on(SOCKET_EVENTS.LINK_INCOMING_LOST, onIncomingLost)
        socket.on(SOCKET_EVENTS.LINK_RECIPROCATED, onReciprocated)
        socket.on(SOCKET_EVENTS.LINK_SEALED, onSealed)
        socket.on(SOCKET_EVENTS.LINK_ABORTED, onAborted)
        socket.on(SOCKET_EVENTS.PLAYER_ANCHOR, onAnchor)
        socket.on(SOCKET_EVENTS.WEBRTC_OFFER_INCOMING, onOffer)
        socket.on(SOCKET_EVENTS.WEBRTC_ANSWER_INCOMING, onAnswer)
        socket.on(SOCKET_EVENTS.WEBRTC_ICE_INCOMING, onIce)

        return () => {
            socket.off(SOCKET_EVENTS.LINK_INCOMING, onIncoming)
            socket.off(SOCKET_EVENTS.LINK_INCOMING_LOST, onIncomingLost)
            socket.off(SOCKET_EVENTS.LINK_RECIPROCATED, onReciprocated)
            socket.off(SOCKET_EVENTS.LINK_SEALED, onSealed)
            socket.off(SOCKET_EVENTS.LINK_ABORTED, onAborted)
            socket.off(SOCKET_EVENTS.PLAYER_ANCHOR, onAnchor)
            socket.off(SOCKET_EVENTS.WEBRTC_OFFER_INCOMING, onOffer)
            socket.off(SOCKET_EVENTS.WEBRTC_ANSWER_INCOMING, onAnswer)
            socket.off(SOCKET_EVENTS.WEBRTC_ICE_INCOMING, onIce)
            closeVoiceConnection()
            link.abort()
        }
    }, [connected])
}

// Helpers que la UI llama directo (no necesitan estado React).
export function reachOut(targetId: string): void {
    const socket = getSocket()
    if (!socket?.connected) return
    socket.emit(SOCKET_EVENTS.LINK_REACH, { targetId })
    useLinkStore.getState().setReaching(targetId)
}

export function releaseReach(): void {
    const socket = getSocket()
    if (socket?.connected) {
        socket.emit(SOCKET_EVENTS.LINK_RELEASE)
    }
    useLinkStore.getState().setReaching(null)
}
